import React, { useEffect, useState } from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import {
  TextField,
  Button,
  Box,
  Typography,
  Paper,
  Grid,
  AppBar,
  Toolbar,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Checkbox,
  Step,
  StepLabel,
  Stepper,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup,
} from '@mui/material';
import { styled, useMediaQuery } from '@mui/system';
import { useAuth } from '../contexts/AuthContext';
import LogoutButton from '../components/LogoutButton';
import { Category } from '../types/categories.types';
import { getCategories } from '../services/categories.services';
import { getActiveRegions } from '../services/regions.services';
import { theme } from '../theme/theme';

const validationSchema = Yup.object().shape({
  firstName: Yup.string().required('First name is required'),
  lastName: Yup.string().required('Last name is required'),
  phone: Yup.string()
    .matches(/^[0-9]{10}$/, 'Phone number must be 10 digits')
    .required('Phone number is required'),
  address: Yup.string().required('Address is required'),
  city: Yup.string().required('City is required'),
  state: Yup.string().required('State is required'),
  zipCode: Yup.string()
    .matches(/^[0-9]{5}$/, 'Zip code must be 5 digits')
    .required('Zip code is required'),
  categories: Yup.array().min(1, 'At least one category must be selected'),
  regionId: Yup.string().required('Region is required'),
  judgingLevel: Yup.string().required('Judging level is required'),
});

//Region interface
interface Region {
  id: string;
  name: string;
  location: string;
  state: string;
}

//Category interface extended to allow filtering based on judging level selection
interface ExtendedCategory extends Category {
  level: 'elementary' | 'seniorHigh' | 'both';
}

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(6),
  },
}));

const StyledForm = styled(Form)({
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
});

const JudgeRegistration: React.FC = () => {
  //Use useAuth to get authStatus and completeRegistration function
  const { authStatus, completeRegistration } = useAuth();
  //State for registration errors
  const [registrationError, setRegistrationError] = useState<string | null>(
    null
  );
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  //Steps for the stepper panel
  const steps = ['Personal Info', 'Category Selection', 'Region Info'];
  const [activeStep, setActiveStep] = useState(0);
  //Regions and categories hooks
  const [regions, setRegions] = useState<Region[]>([]);
  const [categories, setCategories] = useState<ExtendedCategory[]>([]);
  const [filteredCategories, setFilteredCategories] = useState<
    ExtendedCategory[]
  >([]);

  useEffect(() => {
    fetchRegions();
    fetchCategories();
  }, []);

  //Fetch categories for judges to select
  const fetchCategories = async () => {
    try {
      //Get catergories from the database
      const fetchedCategories = await getCategories(null);
      //Map the categories to include the judging level
      setCategories(
        fetchedCategories.map((category) => ({
          ...category,
          level: 'both', //Default value
        }))
      );
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  //Fetch regions that are active for user to select from
  const fetchRegions = async () => {
    try {
      //Get active regions from the database
      const rawData = await getActiveRegions();
      //Map the regions to the Region interface format
      const data: Region[] = rawData.map((region: any) => ({
        id: region.id,
        name: region.name,
        location: region.location,
        state: region.state,
      }));
      //Set the regions hook
      setRegions(data);
    } catch (err) {
      console.error('Error fetching regions:', err);
      setRegistrationError('Failed to load regions. Please try again later.');
    }
  };

  //Values for the form
  const initialValues = {
    firstName: '',
    lastName: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    categories: [] as string[],
    regionId: '',
    judgingLevel: '',
  };

  type FormValues = typeof initialValues;
  const stepFieldMap: Record<number, (keyof FormValues)[]> = {
    0: [
      'firstName',
      'lastName',
      'phone',
      'address',
      'city',
      'state',
      'zipCode',
    ],
    1: ['judgingLevel', 'categories'],
    2: ['regionId'],
  };

  //When the user completes their registration and signs up
  const handleSubmit = async (values: any, { setSubmitting }: any) => {
    try {
      //Reset the error state
      setRegistrationError(null);

      //Check if the user is authenticated
      if (!authStatus.user) {
        throw new Error('User not authenticated');
      }

      //Create the registration data object and set the userType to 'judge'
      const registrationData = {
        ...values,
        userType: 'judge',
      };

      //Submit registration data to complete the registration process (After Registration is completed, AccessGuard will handle navigation to judge dashboard)
      await completeRegistration(registrationData);
    } catch (error) {
      console.error('Registration error:', error);
      setRegistrationError(
        'An error occurred during registration. Please try again.'
      );
      //Set the submitting state to false to allow the user to try again
      setSubmitting(false);
    }
  };

  //Filter categories based on the judging level
  const getFilteredCategories = (judgingLevel: string) => {
    //If no judge level return all categories
    if (!judgingLevel) return [];

    return categories.filter((category) => {
      //If the judging level is 'elementary', return only the elementary categories
      if (judgingLevel === 'elementary') {
        return category.isElementary;
      } //If the judging level is 'seniorHigh', return only the high school categories
      else if (judgingLevel === 'seniorHigh') {
        return category.isHighSchool;
      }
      //If the judging level is 'both', return both elementary and high school categories
      return true;
    });
  };

  return (
    <>
      <AppBar position="static" color="transparent" elevation={0}>
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Typography variant="h6" component="div">
            Judge Registration
          </Typography>
          <LogoutButton />
        </Toolbar>
      </AppBar>
      <Box sx={{ maxWidth: 600, margin: 'auto', mt: 4, px: 2 }}>
        <StyledPaper elevation={3}>
          <Typography variant="h4" gutterBottom align="center" color="primary">
            Complete Your Registration
          </Typography>

          <Stepper
            activeStep={activeStep}
            alternativeLabel={!isMobile}
            orientation={isMobile ? 'vertical' : 'horizontal'}
            sx={{ mb: 4 }}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {registrationError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {registrationError}
            </Alert>
          )}

          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({
              errors,
              touched,
              isSubmitting,
              values,
              setFieldValue,
              setTouched,
            }) => (
              <StyledForm>
                {activeStep === 0 && (
                  <>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Field
                          as={TextField}
                          fullWidth
                          name="firstName"
                          label="First Name"
                          error={touched.firstName && Boolean(errors.firstName)}
                          helperText={touched.firstName && errors.firstName}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Field
                          as={TextField}
                          fullWidth
                          name="lastName"
                          label="Last Name"
                          error={touched.lastName && Boolean(errors.lastName)}
                          helperText={touched.lastName && errors.lastName}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Field
                          as={TextField}
                          fullWidth
                          name="phone"
                          label="Phone Number"
                          error={touched.phone && Boolean(errors.phone)}
                          helperText={touched.phone && errors.phone}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Field
                          as={TextField}
                          fullWidth
                          name="address"
                          label="Address"
                          error={touched.address && Boolean(errors.address)}
                          helperText={touched.address && errors.address}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Field
                          as={TextField}
                          fullWidth
                          name="city"
                          label="City"
                          error={touched.city && Boolean(errors.city)}
                          helperText={touched.city && errors.city}
                        />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <Field
                          as={TextField}
                          fullWidth
                          name="state"
                          label="State"
                          error={touched.state && Boolean(errors.state)}
                          helperText={touched.state && errors.state}
                        />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <Field
                          as={TextField}
                          fullWidth
                          name="zipCode"
                          label="Zip Code"
                          error={touched.zipCode && Boolean(errors.zipCode)}
                          helperText={touched.zipCode && errors.zipCode}
                        />
                      </Grid>
                    </Grid>
                  </>
                )}

                {activeStep === 1 && (
                  <>
                    <Grid container spacing={2} mb={2}>
                      <Grid item xs={12}>
                        <FormControl
                          component="fieldset"
                          error={
                            touched.judgingLevel && Boolean(errors.judgingLevel)
                          }
                          fullWidth
                        >
                          <Typography variant="subtitle1" gutterBottom>
                            Would you like to judge projects from High School,
                            Elementary School, or Both?
                          </Typography>
                          <Field
                            name="judgingLevel"
                            render={({ field }: any) => (
                              <RadioGroup
                                {...field}
                                aria-label="judging-level"
                                onChange={(e) => {
                                  field.onChange(e);
                                  //Clear categories when judging level changes
                                  setFieldValue('categories', []);
                                  //Update filtered categories
                                  const filtered = getFilteredCategories(
                                    e.target.value
                                  );
                                  setFilteredCategories(filtered);
                                }}
                              >
                                <FormControlLabel
                                  value="seniorHigh"
                                  control={<Radio />}
                                  label="High School"
                                />
                                <FormControlLabel
                                  value="elementary"
                                  control={<Radio />}
                                  label="Elementary School"
                                />
                                <FormControlLabel
                                  value="both"
                                  control={<Radio />}
                                  label="Both"
                                />
                              </RadioGroup>
                            )}
                          />
                          {touched.judgingLevel && errors.judgingLevel && (
                            <FormHelperText error>
                              {errors.judgingLevel as string}
                            </FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                    </Grid>

                    {/* Only show category selection if judging level is selected */}
                    {values.judgingLevel && (
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Typography variant="subtitle1" gutterBottom>
                            Select Categories you'd like to Judge!
                          </Typography>
                          <FormControl
                            fullWidth
                            error={
                              touched.categories && Boolean(errors.categories)
                            }
                          >
                            <Select
                              multiple
                              value={values.categories}
                              onChange={(e) =>
                                setFieldValue('categories', e.target.value)
                              }
                              renderValue={(selected) =>
                                filteredCategories
                                  .filter((category) =>
                                    selected.includes(category.id)
                                  )
                                  .map((category) => category.name)
                                  .join(', ')
                              }
                            >
                              {filteredCategories.length > 0 ? (
                                filteredCategories.map((category) => (
                                  <MenuItem
                                    key={category.id}
                                    value={category.id}
                                  >
                                    <Checkbox
                                      checked={values.categories.includes(
                                        category.id
                                      )}
                                    />
                                    {category.name}
                                  </MenuItem>
                                ))
                              ) : (
                                <MenuItem disabled>
                                  No Categories Available for this level
                                </MenuItem>
                              )}
                            </Select>
                            {touched.categories && errors.categories && (
                              <FormHelperText error>
                                {errors.categories as string}
                              </FormHelperText>
                            )}
                          </FormControl>
                        </Grid>
                      </Grid>
                    )}
                  </>
                )}

                {activeStep === 2 && (
                  <>
                    <FormControl
                      fullWidth
                      error={touched.regionId && Boolean(errors.regionId)}
                    >
                      <InputLabel id="region-select-label">Region</InputLabel>
                      <Field
                        as={Select}
                        labelId="region-select-label"
                        id="region-select"
                        name="regionId"
                        label="Region"
                      >
                        {regions.length > 0 ? (
                          regions.map((region) => (
                            <MenuItem key={region.id} value={region.id}>
                              {region.name} ({region.location}, {region.state})
                            </MenuItem>
                          ))
                        ) : (
                          <MenuItem disabled value="">
                            Loading regions...
                          </MenuItem>
                        )}
                      </Field>
                      {touched.regionId && errors.regionId && (
                        <FormHelperText error>
                          {errors.regionId as string}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </>
                )}

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mt: 2,
                  }}
                >
                  <Button
                    variant="outlined"
                    color="primary"
                    disabled={activeStep === 0}
                    onClick={() => setActiveStep((prev) => prev - 1)}
                  >
                    Back
                  </Button>
                  {activeStep === steps.length - 1 ? (
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Submitting...' : 'Complete Registration'}
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      variant="contained"
                      color="primary"
                      onClick={async () => {
                        const fieldsToCheck = stepFieldMap[activeStep];
                        const stepSchema = validationSchema.pick(fieldsToCheck);

                        try {
                          await stepSchema.validate(values, {
                            abortEarly: false,
                          });

                          // Mark step fields as touched
                          const touchedFields = fieldsToCheck.reduce(
                            (acc, field) => {
                              acc[field] = true;
                              return acc;
                            },
                            {} as Record<keyof FormValues, boolean>
                          );

                          setTouched(touchedFields, true);

                          // Advance step if validation passes
                          setActiveStep((prev) => prev + 1);
                        } catch (validationError: any) {
                          // Mark all fields in error as touched
                          const touchedFields = validationError.inner.reduce(
                            (acc: any, error: any) => {
                              acc[error.path] = true;
                              return acc;
                            },
                            {}
                          );
                          setTouched(touchedFields, true);
                        }
                      }}
                    >
                      Next
                    </Button>
                  )}
                </Box>
              </StyledForm>
            )}
          </Formik>
        </StyledPaper>
      </Box>
    </>
  );
};

export default JudgeRegistration;
